import type { AddAction } from '@/root/shared-providers/BookingCart/reducer'
import type { CartItemSailParam } from '@/root/shared-providers/BookingCart/types'
import type { Guest, Reservation, ReservationItem } from '@/root/shared-types'
import type { PassengerFormData } from '@/root/shared-types/selectTicketPage/selectTicket'
import { isUserVisibleItem } from '@/root/shared-helpers/reservation/reservation-item-filters'

/**
 * The ticket items of a reservation, one per price category: a round trip returns one item per sail package,
 * so later items of a category are skipped.
 */
const firstItemPerCategory = (reservation: Reservation, accept: (item: ReservationItem) => boolean = () => true) => {
  const seen = new Set<string>()
  return (reservation.items ?? []).filter((item) => {
    if (!isUserVisibleItem(item) || !accept(item) || seen.has(item.priceCategory)) return false
    seen.add(item.priceCategory)
    return true
  })
}

const isDeck = (item: ReservationItem) => item.priceCategorySubType === 'DECK'

const guestsBySeqN = (reservation: Reservation) =>
  new Map((reservation.guests ?? []).map((guest) => [guest.seqN, guest]))

/** A booked car or trailer item, not a bicycle. */
const isCarDeckVehicleItem = (item: ReservationItem) =>
  item.type === 'VEHICLE' && (item.priceCategorySubType === 'VEHICLE' || item.priceCategorySubType === 'TRAILER')

/**
 * A cart vehicle per booked car-deck vehicle, with its plate and dimensions. The next request must send them
 * again with the same seqN, or the backend removes the vehicle. A round trip books one item per sail package
 * for the same vehicle, so a vehicle comes once.
 */
const getCarDeckVehicles = (reservation: Reservation, sailPackages: CartItemSailParam[]): AddAction['payload'][] => {
  const vehiclesBySeqN = new Map((reservation.vehicles ?? []).map((vehicle) => [vehicle.seqN, vehicle]))
  const seen = new Set<number>()

  return (reservation.items ?? []).filter(isUserVisibleItem).flatMap((item): AddAction['payload'][] => {
    if (!isCarDeckVehicleItem(item)) return []
    return (item.ownerSeqNs ?? []).flatMap((seqN): AddAction['payload'][] => {
      const vehicle = vehiclesBySeqN.get(seqN)
      if (!vehicle || vehicle.type === 'BICYCLE' || seen.has(seqN)) return []
      seen.add(seqN)
      return [
        {
          itemClass: 'vehicles',
          quantity: 1,
          seqNum: seqN,
          type: '',
          subType: item.priceCategorySubType,
          title: item.priceCategoryTranslation,
          price: item.pricePerUnit,
          priceCategory: item.priceCategory,
          sailPackages,
          licencePlate: vehicle.licencePlate,
          lengthInCm: vehicle.lengthInCm,
          widthInCm: vehicle.widthInCm,
          heightInCm: vehicle.heightInCm,
          weightInKg: vehicle.weightInKg,
        },
      ]
    })
  })
}

/** The booking cart items of a reservation: a passenger per deck guest, a vehicle per booked bicycle or car-deck vehicle. */
export const getCartItemsFromReservation = (
  reservation: Reservation,
  sailPackages: CartItemSailParam[]
): AddAction['payload'][] => {
  const guests = guestsBySeqN(reservation)

  const ticketItems = firstItemPerCategory(reservation).flatMap((item): AddAction['payload'][] => {
    if (item.priceCategorySubType === 'DECK') {
      return (item.ownerSeqNs ?? []).map((seqN) => {
        const guest = guests.get(seqN)
        return {
          itemClass: 'passengers',
          quantity: 1,
          seqNum: seqN,
          type: '',
          subType: item.priceCategorySubType,
          title: item.priceCategoryTranslation,
          price: item.pricePerUnit,
          priceCategory: item.priceCategory,
          passengerId: guest?.personalIdentificationNumber ?? '',
          sailPackages,
          firstName: guest?.firstName,
          lastName: guest?.lastName,
          birthday: guest?.birthday,
        }
      })
    }
    if (item.priceCategorySubType === 'BICYCLE') {
      return Array.from({ length: item.quantity }, () => ({
        itemClass: 'vehicles',
        quantity: 1,
        type: '',
        subType: item.priceCategorySubType,
        title: item.priceCategoryTranslation,
        price: item.pricePerUnit,
        priceCategory: item.priceCategory,
        sailPackages,
      }))
    }
    return []
  })

  // The items with a booked seqN go first. A bicycle has no seqN here and gets the next free one from the cart, so it
  // must come after every booked seqN, or it can take the seqN of a booked car.
  const items = [...ticketItems, ...getCarDeckVehicles(reservation, sailPackages)]
  return [...items.filter((item) => item.seqNum !== undefined), ...items.filter((item) => item.seqNum === undefined)]
}

/** The passenger form of a booked guest. */
export const toPassengerFormData = (
  guest: Guest | undefined,
  item: ReservationItem,
  seqN: number
): PassengerFormData => ({
  firstName: guest?.firstName ?? '',
  lastName: guest?.lastName ?? '',
  birthdate: guest?.birthday ?? '',
  citizenship: guest?.citizenship ?? '',
  personalId: guest?.personalIdentificationNumber ?? '',
  gender: guest?.gender,
  priceCategory: item.priceCategory,
  additionalInfo: guest?.comments ?? '',
  personalIdConfirmed: item.resident,
  seqN,
})

/** Saved passenger forms by price category and guest seqN. */
export const getPassengersFormData = (reservation: Reservation): Record<string, Record<number, PassengerFormData>> => {
  const guests = guestsBySeqN(reservation)

  return Object.fromEntries(
    firstItemPerCategory(reservation, isDeck).map((item) => [
      item.priceCategory,
      Object.fromEntries(
        (item.ownerSeqNs ?? []).map((seqN) => [seqN, toPassengerFormData(guests.get(seqN), item, seqN)])
      ),
    ])
  )
}

// Every resident ticket shows on the one resident card, keyed by the first resident category
const cardCode = (item: ReservationItem, firstResidentCode?: string) =>
  item.resident && firstResidentCode ? firstResidentCode : item.priceCategory

/** Booked quantity per card, to prefill the cards from an existing reservation. */
export const getInitialQuantityByCode = (reservation: Reservation | undefined, firstResidentCode?: string) => {
  const result: Record<string, number> = {}
  if (!reservation) return result
  for (const item of firstItemPerCategory(reservation)) {
    const key = cardCode(item, firstResidentCode)
    result[key] = (result[key] ?? 0) + item.quantity
  }
  return result
}

/** Booked passengers per card, to prefill the passenger forms from an existing reservation. */
export const getInitialPassengersByCode = (reservation: Reservation | undefined, firstResidentCode?: string) => {
  const result: Record<string, PassengerFormData[]> = {}
  if (!reservation) return result
  const guests = guestsBySeqN(reservation)
  for (const item of firstItemPerCategory(reservation, isDeck)) {
    const key = cardCode(item, firstResidentCode)
    result[key] = [
      ...(result[key] ?? []),
      ...(item.ownerSeqNs ?? []).map((seqN) => toPassengerFormData(guests.get(seqN), item, seqN)),
    ]
  }
  return result
}

/** Guest seqNs per card, from every deck item (both legs of a round trip). */
export const getSeqNsByCode = (items: ReservationItem[] = [], firstResidentCode?: string) => {
  const result: Record<string, number[]> = {}
  for (const item of items) {
    if (!isUserVisibleItem(item) || !isDeck(item)) continue
    const key = cardCode(item, firstResidentCode)
    result[key] = [...(result[key] ?? []), ...(item.ownerSeqNs ?? [])]
  }
  return result
}
